import posthog from 'posthog-js';

/**
 * PostHog wiring, scoped to honor the public privacy policy (/privacy).
 *
 * Two consent regimes, deliberately split because PostHog's native opt-out
 * suppresses *everything* (events AND exceptions):
 *
 *   - Error/crash diagnostics — collected from ALL visitors (incl. signed-out)
 *     as legitimate-interest stability data. The instance therefore inits in a
 *     capturing state, and exception autocapture (`capture_exceptions`) flows
 *     for everyone.
 *   - Product usage events — on by default for signed-in accounts, opt-out in
 *     Settings. Gated by our own `analyticsConsented` flag (set via
 *     AnalyticsConsentSync), NOT PostHog's opt-out, so gating events doesn't
 *     also kill error capture.
 *
 * Config keeps it anonymous and minimal: US host, no autocapture, no session
 * replay, no automatic pageviews, `identified_only` profiles. Because the
 * instance is always capturing, NO code may call `posthog.capture()` directly —
 * route every product event through `captureEvent` so the consent gate holds.
 *
 * With no API key configured (dev/test/preview), every export is a no-op — the
 * SDK never loads and nothing leaves the browser.
 */
const DEFAULT_HOST = 'https://us.i.posthog.com';

let initialized = false;
// Product-event consent. Errors are NOT gated by this — only `captureEvent` is.
let analyticsConsented = false;

export function initAnalytics(): void {
  if (initialized) return;
  const key = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
  if (!key) return; // No key → analytics fully disabled.
  posthog.init(key, {
    api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST ?? DEFAULT_HOST,
    // Intentionally NOT opt_out_by_default: the instance captures so crash/error
    // diagnostics work for every visitor. Product *events* are gated separately
    // by analyticsConsented (see captureEvent) — never by PostHog's opt-out.
    autocapture: false,
    disable_session_recording: true,
    capture_pageview: false,
    // Error tracking: autocapture unhandled errors + promise rejections for all
    // visitors. Console errors stay off to avoid noise.
    capture_exceptions: true,
    // Stay anonymous: never create a person profile unless we identify (we
    // don't), keeping data un-attributable per the "anonymized" promise.
    person_profiles: 'identified_only',
  });
  initialized = true;
}

/** Set product-event consent (errors are always captured, independent of this). */
export function setAnalyticsConsent(enabled: boolean): void {
  analyticsConsented = enabled;
}

/** Capture an anonymized product event. No-op until initialized + analytics-consented. */
export function captureEvent(event: string, properties?: Record<string, unknown>): void {
  if (!initialized || !analyticsConsented) return;
  posthog.capture(event, properties);
}

/**
 * Manually report an exception to PostHog error tracking — for errors caught in
 * boundaries/catch blocks that never reach the global autocapture handler.
 * Captured for all visitors (not analytics-consent gated). No-op until initialized.
 */
export function captureException(error: unknown, properties?: Record<string, unknown>): void {
  if (!initialized) return;
  posthog.captureException(error, properties);
}
